<template>
    <div class="dashboard-page">
        <teleport :to="customizeTarget" v-if="customizeTargetReady">
            <button @click="ds.toggleCustomize()"
                    :class="['dashboard-customize-btn', { 'dashboard-customize-btn--in-title': isMobile }]"
                    :title="$t('common.dashboard.customizeTooltip')">
                <i class="bi bi-grid-1x2"></i>
                <span>{{ $t('common.dashboard.customize') }}</span>
            </button>
        </teleport>

        <div class="dashboard-grid" :class="{ 'dw-glass-mode': ds.state.glassMode }" ref="grid">
            <component v-for="row in visibleRows"
                :is="row.component"
                :key="row.id"
                :instance-id="row.id"
                :card-class="cardClass(row)"
                :card-style="widgetStyle(row)"
                :ref="el => { if (el) widgetRefs[row.id] = el.$el || el }"
                @header-pointerdown="onDragStart($event, row)"
                @resize-pointerdown="onResizeStart($event, row)"
            />
        </div>

        <div v-if="dragState.ghost" class="dw-ghost" :style="dragState.ghostStyle" v-html="dragState.ghostHtml"></div>

        <DashboardCustomizeDrawer />
    </div>
</template>

<script>
import dashboardService from '@/components/dashboard/dashboardService';
import DashboardCustomizeDrawer from '@/components/dashboard/DashboardCustomizeDrawer.vue';

export default {
    name: 'dashboard',
    components: { DashboardCustomizeDrawer },

    data() {
        return {
            ds: dashboardService,
            isMobile: false,
            customizeTargetReady: false,
            widgetRefs: {},
            dragState: {
                dragging: false, id: null, overId: null,
                ghost: false, ghostHtml: '', ghostStyle: {},
                startX: 0, startY: 0, started: false, itemRects: {},
            },
            resizeState: { active: false, id: null, startX: 0, startY: 0, startColSpan: 1, startRowSpan: 1 },
        };
    },

    computed: {
        customizeTarget() {
            return this.isMobile ? '.page-title' : '#kt_toolbar_actions';
        },
        visibleRows() {
            const insts = dashboardService.getVisibleInstances();
            return insts.map(i => {
                const tpl = dashboardService.getTemplates().find(t => t.id === i.templateId);
                if (!tpl) return null;
                return {
                    id: i.id,
                    templateId: i.templateId,
                    component: tpl.component,
                    defaultCol: tpl.defaultCol,
                    defaultRow: tpl.defaultRow,
                    glassRgb: tpl.glassRgb,
                };
            }).filter(Boolean);
        },
    },

    methods: {
        widgetStyle(row) {
            const sizes = this.ds.state.sizes[row.id] || {};
            const col = sizes.col || row.defaultCol;
            const r = sizes.row || row.defaultRow;
            const s = { gridColumn: 'span ' + col, gridRow: 'span ' + r };
            if (this.ds.state.glassMode && row.glassRgb) s['--cg'] = row.glassRgb;
            return s;
        },

        _checkTarget() {
            this.customizeTargetReady = !!document.querySelector(this.customizeTarget);
        },

        cardClass(row) {
            const sizes = this.ds.state.sizes[row.id];
            return {
                'dw-dragging': this.dragState.dragging && this.dragState.id === row.id,
                'dw-drag-over': this.dragState.dragging && this.dragState.overId === row.id,
                'dw-resized': (sizes && sizes.row > 1),
                'dw-resizing': this.resizeState.active && this.resizeState.id === row.id,
            };
        },

        onDragStart(e, w) {
            if (e.target.closest('.dw-wo-select, .dw-wo-limit, .el-select, .el-input-number')) return;

            const isTouch = e.pointerType === 'touch';
            if (isTouch && !e.target.closest('.dw-drag-handle')) return;

            e.preventDefault();

            const el = this.widgetRefs[w.id];
            if (!el) return;
            this.dragState.startX = e.clientX;
            this.dragState.startY = e.clientY;
            this.dragState.id = w.id;
            this.dragState.started = false;

            const elW = el.offsetWidth;
            const onMove = (ev) => {
                const dx = Math.abs(ev.clientX - this.dragState.startX);
                const dy = Math.abs(ev.clientY - this.dragState.startY);
                if (!this.dragState.started && (dx > 5 || dy > 5)) {
                    this.dragState.started = true;
                    this.dragState.dragging = true;
                    this.dragState.ghost = true;
                    this.dragState.ghostHtml = el.innerHTML;
                    this._snapshotRects();
                }
                if (this.dragState.started) {
                    this.dragState.ghostStyle = { position: 'fixed', left: (ev.clientX - elW / 2) + 'px', top: (ev.clientY - 30) + 'px', width: elW + 'px', zIndex: 9999, pointerEvents: 'none' };
                    this.dragState.overId = this._findOverWidget(ev.clientX, ev.clientY);
                    this._handleAutoScroll(ev.clientY);
                }
            };

            const onUp = () => {
                document.removeEventListener('pointermove', onMove);
                document.removeEventListener('pointerup', onUp);
                document.removeEventListener('pointercancel', onUp);
                this._stopAutoScroll();
                if (this.dragState.started && this.dragState.overId && this.dragState.overId !== this.dragState.id) {
                    const current = this.ds.state.instances.map(i => i.id);
                    const fromIdx = current.indexOf(this.dragState.id);
                    const toIdx = current.indexOf(this.dragState.overId);
                    if (fromIdx !== -1 && toIdx !== -1) {
                        current.splice(fromIdx, 1);
                        current.splice(toIdx, 0, this.dragState.id);
                        this.ds.setOrder(current);
                    }
                }
                this.dragState = { dragging: false, id: null, overId: null, ghost: false, ghostHtml: '', ghostStyle: {}, startX: 0, startY: 0, started: false, itemRects: {} };
            };

            document.addEventListener('pointermove', onMove);
            document.addEventListener('pointerup', onUp);
            document.addEventListener('pointercancel', onUp);
        },

        _handleAutoScroll(y) {
            const edge = 60;
            const maxSpeed = 10;
            const vh = window.innerHeight;
            if (y < edge) {
                this._autoScrollSpeed = -maxSpeed * (1 - y / edge);
            } else if (y > vh - edge) {
                this._autoScrollSpeed = maxSpeed * ((y - (vh - edge)) / edge);
            } else {
                this._autoScrollSpeed = 0;
            }
            if (this._autoScrollSpeed && !this._autoScrollRaf) {
                const tick = () => {
                    if (!this._autoScrollSpeed || !this.dragState.started) { this._autoScrollRaf = null; return; }
                    window.scrollBy(0, this._autoScrollSpeed);
                    this._snapshotRects();
                    this._autoScrollRaf = requestAnimationFrame(tick);
                };
                this._autoScrollRaf = requestAnimationFrame(tick);
            }
        },

        _stopAutoScroll() {
            this._autoScrollSpeed = 0;
            if (this._autoScrollRaf) { cancelAnimationFrame(this._autoScrollRaf); this._autoScrollRaf = null; }
        },

        _snapshotRects() {
            const rects = {};
            this.visibleRows.forEach(row => {
                const el = this.widgetRefs[row.id];
                if (el) {
                    const r = el.getBoundingClientRect();
                    rects[row.id] = { left: r.left, top: r.top, right: r.right, bottom: r.bottom, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
                }
            });
            this.dragState.itemRects = rects;
        },

        _findOverWidget(x, y) {
            let closest = null;
            let minDist = Infinity;
            for (const [id, rect] of Object.entries(this.dragState.itemRects)) {
                if (id === this.dragState.id) continue;
                if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                    const dist = Math.hypot(x - rect.cx, y - rect.cy);
                    if (dist < minDist) { minDist = dist; closest = id; }
                }
            }
            return closest;
        },

        onResizeStart(e, w) {
            const el = this.widgetRefs[w.id];
            if (!el) return;

            const sizes = this.ds.state.sizes[w.id] || {};
            const limits = this.ds.LIMITS;
            this.resizeState = { active: true, id: w.id, startX: e.clientX, startY: e.clientY, startColSpan: sizes.col || w.defaultCol, startRowSpan: sizes.row || w.defaultRow };
            const gridEl = this.$refs.grid;
            const gridRect = gridEl.getBoundingClientRect();
            const cols = getComputedStyle(gridEl).gridTemplateColumns.split(' ').length;
            const colWidth = gridRect.width / cols;
            const gap = parseFloat(getComputedStyle(gridEl).gap) || 20;
            const rowHeight = colWidth * 0.7;
            const self = this;

            const onMove = function(ev) {
                const dx = ev.clientX - self.resizeState.startX;
                const dy = ev.clientY - self.resizeState.startY;
                const newCol = Math.max(limits.MIN_COL, Math.min(limits.MAX_COL, self.resizeState.startColSpan + Math.round(dx / (colWidth + gap))));
                const newRow = Math.max(limits.MIN_ROW, Math.min(limits.MAX_ROW, self.resizeState.startRowSpan + Math.round(dy / (rowHeight + gap))));
                const cur = self.ds.state.sizes[w.id];
                if (!cur || cur.col !== newCol || cur.row !== newRow) {
                    self.ds.setSize(w.id, { col: newCol, row: newRow });
                }
            };

            const onUp = function() {
                document.removeEventListener('pointermove', onMove);
                document.removeEventListener('pointerup', onUp);
                self.resizeState.active = false;
            };

            document.addEventListener('pointermove', onMove);
            document.addEventListener('pointerup', onUp);
        },
    },

    mounted() {
        this.setCurrentPageTitle(this.$t('menu.dashboard'));
        this.ds.init();
        this.ds.loadAll();
        this.ds.startAutoRefresh();

        this._mq = window.matchMedia('(max-width: 991.98px)');
        this._mqHandler = () => { this.isMobile = this._mq.matches; this._checkTarget(); };
        this._mq.addEventListener('change', this._mqHandler);
        this.isMobile = this._mq.matches;
        this.$nextTick(() => this._checkTarget());
    },

    beforeUnmount() {
        this.widgetRefs = {};
        this.ds.stopAutoRefresh();
        dashboardService.closeCustomize();
        if (this._mq && this._mqHandler) this._mq.removeEventListener('change', this._mqHandler);
    },
};
</script>

<style>
.dashboard-customize-btn--in-title {
    margin-left: auto;
    align-self: center;
}

.dashboard-customize-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 11px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    border-radius: 6px;
    background: var(--panel-accent);
    color: #fff;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.1s;
}
.dashboard-customize-btn:hover { opacity: 0.85; }
.dashboard-customize-btn:active { transform: scale(0.97); }
.dashboard-customize-btn .bi { font-size: 13px; }

.dashboard-page { padding: 4px 0; background: #f4f6f9; min-height: 100vh; }
html.dark .dashboard-page,
html.midnight .dashboard-page { background: transparent; }
html.cream .dashboard-page { background: transparent; }

.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: minmax(180px, auto);
    grid-auto-flow: dense;
    gap: 20px;
    border-radius: 16px;
    padding: 4px;
    margin: -4px;
}
@media (max-width: 1400px) { .dashboard-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 992px)  { .dashboard-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 576px) {
    .dashboard-grid { grid-template-columns: 1fr; }
    .dw-card { grid-column: span 1 !important; }
}

.dw-card {
    background: var(--bs-body-bg);
    border: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12);
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.06), 0 8px 30px rgba(0,0,0,0.03);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    isolation: isolate;
    container-type: inline-size;
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s, opacity 0.2s;
}
.dw-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08), 0 8px 28px rgba(0,0,0,0.06), 0 16px 48px rgba(0,0,0,0.03); }
.dw-card.dw-dragging {
    opacity: 0.25; transform: scale(0.96);
    border: 2px dashed rgba(59, 130, 246, 0.3);
    box-shadow: none;
    transition: none;
}
.dw-card.dw-dragging > * { opacity: 0.3; }

.dw-card.dw-drag-over {
    transform: scale(1.03);
    border-color: rgba(59, 130, 246, 0.5);
    animation: dw-drop-pulse 1.8s ease-in-out infinite;
    z-index: 2;
    transition: none;
}
.dw-card.dw-drag-over::after {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 50% 40%, rgba(59, 130, 246, 0.08) 0%, transparent 70%);
    pointer-events: none; z-index: 50;
}

@keyframes dw-drop-pulse {
    0%, 100% {
        box-shadow:
            0 0 0 2px rgba(59, 130, 246, 0.25),
            0 0 16px rgba(59, 130, 246, 0.08),
            0 8px 24px rgba(0, 0, 0, 0.06);
    }
    50% {
        box-shadow:
            0 0 0 3px rgba(59, 130, 246, 0.5),
            0 0 32px rgba(59, 130, 246, 0.18),
            0 8px 24px rgba(0, 0, 0, 0.06);
    }
}

.dw-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 0;
    cursor: grab;
    user-select: none;
}
@media (pointer: fine) {
    .dw-header { touch-action: none; }
}
.dw-header:active { cursor: grabbing; }
.dw-header-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.dw-header-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }

.dw-icon-wrap {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; flex-shrink: 0;
}
.dw-title { font-size: 13px; font-weight: 600; color: var(--bs-body-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dw-drag-handle { color: var(--bs-gray-400); font-size: 16px; opacity: 0; transition: opacity 0.15s; touch-action: none; }
.dw-card:hover .dw-drag-handle { opacity: 0.6; }
@media (pointer: coarse) {
    .dw-drag-handle { opacity: 0.45; font-size: 20px; padding: 8px; margin: -8px; }
}

.dw-body { flex: 1; padding: 14px 16px 16px; display: flex; flex-direction: column; min-height: 0; }

.dw-metrics {
    display: flex; align-items: stretch;
    border-top: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12);
    padding-top: 12px;
}
.dw-metrics--bottom { margin-top: auto; }
.dw-metric { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; text-align: center; }
.dw-metric .bi { font-size: 17px; }
.dw-metric-text { display: flex; flex-direction: column; align-items: center; gap: 1px; }
.dw-metric-val { font-size: 15px; font-weight: 700; color: var(--bs-body-color); line-height: 1.2; }
.dw-metric-lbl { font-size: 10px; color: var(--bs-gray-500); }
.dw-metric-sep { width: 1px; background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12); margin: 0 4px; }

.dw-resize-handle {
    position: absolute; right: 4px; bottom: 4px;
    width: 18px; height: 18px;
    display: flex; align-items: center; justify-content: center;
    cursor: nwse-resize; opacity: 0; transition: opacity 0.15s;
    color: var(--bs-gray-400); font-size: 10px;
    touch-action: none;
}
.dw-card:hover .dw-resize-handle { opacity: 0.5; }

.dw-resizing, .dw-resizing * { transition: none !important; }
.dw-resizing .dw-summary-seg:hover { opacity: 1; transform: none; }

.dw-resized .dw-lowstock-tbody,
.dw-resized .dw-agenda-list,
.dw-resized .dw-wo-list { max-height: none; flex: 1; min-height: 0; }

.dw-ghost {
    background: var(--bs-body-bg); border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 14px;
    box-shadow:
        0 0 0 1px rgba(59, 130, 246, 0.15),
        0 12px 40px rgba(0, 0, 0, 0.2),
        0 4px 12px rgba(59, 130, 246, 0.08);
    opacity: 0.92; transform: scale(1.04) rotate(1.5deg);
    overflow: hidden;
    will-change: left, top;
}

@keyframes dw-fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.dw-fade-in { animation: dw-fade-in 0.3s ease both; }
.dw-card:nth-child(1) { animation-delay: 0s; }
.dw-card:nth-child(2) { animation-delay: 0.03s; }
.dw-card:nth-child(3) { animation-delay: 0.06s; }
.dw-card:nth-child(4) { animation-delay: 0.09s; }
.dw-card:nth-child(5) { animation-delay: 0.12s; }
.dw-card:nth-child(6) { animation-delay: 0.15s; }
.dw-card:nth-child(7) { animation-delay: 0.18s; }
.dw-card:nth-child(8) { animation-delay: 0.21s; }
.dw-card:nth-child(9) { animation-delay: 0.24s; }
.dw-card:nth-child(10) { animation-delay: 0.27s; }
.dw-card:nth-child(11) { animation-delay: 0.30s; }
.dw-card:nth-child(12) { animation-delay: 0.33s; }
.dw-card:nth-child(13) { animation-delay: 0.36s; }
.dw-card:nth-child(14) { animation-delay: 0.39s; }
.dw-card:nth-child(15) { animation-delay: 0.42s; }
.dw-card:nth-child(16) { animation-delay: 0.45s; }
.dw-card:nth-child(17) { animation-delay: 0.48s; }

.dw-finance-body { flex: 1; display: flex; flex-direction: column; justify-content: space-between; }

.dw-stat-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.dw-stat-item { display: flex; flex-direction: column; gap: 3px; }
.dw-stat-label { font-size: 11px; color: var(--bs-gray-500); font-weight: 500; }
.dw-stat-value { font-size: 15px; font-weight: 700; letter-spacing: -0.3px; }

.dw-sparkline-wrap { margin-top: auto; margin-left: -16px; margin-right: -16px; margin-bottom: -16px; overflow: hidden; border-radius: 0 0 11px 11px; }

.dw-ring-bg { opacity: 0.75; }
html.dark .dw-ring-bg { opacity: 0.2; }

.dw-ring-layout { flex: 1; display: flex; align-items: center; gap: 20px; }
.dw-ring-svg { width: 120px; height: 120px; flex-shrink: 0; }
.dw-ring-svg text { text-rendering: geometricPrecision; }
html.dark .dw-ring-svg text, html.midnight .dw-ring-svg text { stroke: none; paint-order: stroke fill; filter: none; -webkit-font-smoothing: antialiased; }

.dw-ring-details { flex: 1; display: flex; flex-direction: column; gap: 0; }
.dw-ring-detail { display: flex; flex-direction: column; gap: 2px; padding: 8px 0; }
.dw-ring-detail-lbl { font-size: 11px; color: var(--bs-gray-500); }
.dw-ring-detail-val { font-size: 15px; font-weight: 700; color: var(--bs-body-color); }
.dw-ring-detail-divider { height: 1px; background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12); }

.dw-pm-layout { flex: 1; display: flex; flex-direction: column; gap: 14px; }
.dw-pm-header { display: flex; flex-direction: column; gap: 12px; }
.dw-pm-hero {
    display: flex; align-items: center; justify-content: space-between;
    background: linear-gradient(135deg, rgba(var(--bs-success-rgb, 80, 205, 137), 0.10), rgba(var(--bs-success-rgb, 80, 205, 137), 0.04));
    border: 1px solid rgba(var(--bs-success-rgb, 80, 205, 137), 0.15);
    border-radius: 10px; padding: 12px 14px;
}
.dw-pm-hero-left { display: flex; flex-direction: column; gap: 2px; }
.dw-pm-total-val { font-size: 22px; font-weight: 800; color: var(--bs-body-color); letter-spacing: -0.5px; line-height: 1.1; }
.dw-pm-total-lbl { font-size: 11px; font-weight: 500; color: var(--bs-gray-500); }
.dw-pm-hero-badge {
    display: flex; align-items: center; gap: 3px;
    font-size: 12px; font-weight: 700; color: var(--bs-success);
    background: rgba(var(--bs-success-rgb, 80, 205, 137), 0.12);
    padding: 4px 8px; border-radius: 6px;
}
.dw-pm-hero-badge .bi { font-size: 11px; }
.dw-pm-list { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.dw-pm-row { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 6px; transition: background 0.15s; }
.dw-pm-row:hover { background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.04); }
.dw-pm-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dw-pm-name { font-size: 12px; color: var(--bs-body-color); flex: 1; }
.dw-pm-pct { font-size: 11px; color: var(--bs-gray-500); flex-shrink: 0; min-width: 30px; text-align: right; }
.dw-pm-val { font-size: 12px; font-weight: 700; color: var(--bs-body-color); flex-shrink: 0; min-width: 60px; text-align: right; }

.dw-cash-body { flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 16px; }

.dw-cash-hero { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.dw-cash-hero-label { font-size: 11px; color: var(--bs-gray-500); font-weight: 500; }
.dw-cash-hero-value { font-size: 26px; font-weight: 800; color: var(--bs-body-color); letter-spacing: -0.5px; }

.dw-cash-bars { display: flex; flex-direction: column; gap: 10px; }
.dw-cash-bar-item { display: flex; flex-direction: column; gap: 4px; }
.dw-cash-bar-head { display: flex; align-items: center; justify-content: space-between; }
.dw-cash-bar-lbl { font-size: 12px; color: var(--bs-body-color); display: flex; align-items: center; gap: 6px; }
.dw-cash-bar-lbl .bi { font-size: 14px; }
.dw-cash-bar-val { font-size: 13px; font-weight: 700; }
.dw-cash-bar-track { height: 6px; border-radius: 3px; background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.1); overflow: hidden; }
.dw-cash-bar-fill { height: 100%; border-radius: 3px; transition: width 0.4s ease; }

.dw-stock-body { flex: 1; display: flex; flex-direction: column; justify-content: space-between; }

.dw-stock-center { display: flex; flex-direction: column; align-items: center; gap: 2px; flex: 1; justify-content: center; }
.dw-stock-big-num { font-size: 40px; font-weight: 800; color: var(--bs-body-color); letter-spacing: -1px; line-height: 1; }
.dw-stock-big-lbl { font-size: 11px; font-weight: 500; color: var(--bs-gray-500); text-transform: uppercase; letter-spacing: 0.5px; }

.dw-lowstock-table { flex: 1; display: flex; flex-direction: column; min-height: 0; }

.dw-lowstock-thead {
    display: flex; align-items: center; gap: 8px;
    padding: 0 10px 8px;
    border-bottom: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12);
}
.dw-lowstock-th { font-size: 10px; font-weight: 600; color: var(--bs-gray-500); text-transform: uppercase; letter-spacing: 0.3px; }
.dw-lowstock-th--name { flex: 1; min-width: 0; }
.dw-lowstock-th--stock { width: 80px; text-align: right; flex-shrink: 0; }
.dw-lowstock-th--status { width: 54px; text-align: center; flex-shrink: 0; }

.dw-lowstock-tbody { flex: 1; display: flex; flex-direction: column; min-height: 0; overflow-y: auto; }

.dw-lowstock-tr {
    display: flex; align-items: center; gap: 8px;
    padding: 7px 10px;
    border-bottom: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.06);
    transition: background 0.15s;
    cursor: pointer;
}
.dw-lowstock-tr:last-child { border-bottom: none; }
.dw-lowstock-tr:hover { background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.05); }
.dw-lowstock-tr--critical { background: rgba(var(--bs-danger-rgb, 241, 65, 108), 0.04); }
.dw-lowstock-tr--critical:hover { background: rgba(var(--bs-danger-rgb, 241, 65, 108), 0.08); }

.dw-lowstock-td--name { flex: 1; min-width: 0; font-size: 12px; font-weight: 500; color: var(--bs-body-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dw-lowstock-td--stock { width: 80px; text-align: right; font-size: 11px; font-weight: 500; color: var(--bs-gray-500); font-variant-numeric: tabular-nums; white-space: nowrap; flex-shrink: 0; }
.dw-lowstock-td--status { width: 54px; text-align: center; flex-shrink: 0; }

.dw-lowstock-chip {
    display: inline-flex; align-items: center; justify-content: center; gap: 3px;
    font-size: 9px; font-weight: 600; padding: 2px 0; border-radius: 4px;
    white-space: nowrap; width: 54px;
}
.dw-lowstock-chip .bi { font-size: 9px; }
.dw-lowstock-chip--danger { background: rgba(var(--bs-danger-rgb, 241, 65, 108), 0.12); color: var(--bs-danger); }
.dw-lowstock-chip--warn { background: rgba(var(--bs-warning-rgb, 255, 199, 0), 0.12); color: var(--bs-warning); }

.dw-weather {
    flex: 1; display: flex; flex-direction: column;
    position: relative; overflow: hidden;
    margin: -14px -16px -16px; padding: 16px 16px 16px;
    margin-top: -66px; padding-top: 74px;
    --wc1: transparent; --wc2: transparent; --wc3: transparent;
    background: linear-gradient(135deg, var(--wc1) 0%, var(--wc2) 50%, var(--wc3) 100%);
    transition: --wc1 0.8s ease, --wc2 0.8s ease, --wc3 0.8s ease;
}
.dw-weather::before {
    content: ''; position: absolute; inset: 0;
    border-radius: 10px; pointer-events: none; opacity: 0.06;
    transition: opacity 0.3s;
}

.dw-weather--clear,
.dw-weather--cloudy,
.dw-weather--overcast,
.dw-weather--rain,
.dw-weather--snow,
.dw-weather--night_clear { color: #fff; }

.dw-weather--clear::before,
.dw-weather--cloudy::before,
.dw-weather--overcast::before,
.dw-weather--rain::before,
.dw-weather--snow::before,
.dw-weather--night_clear::before { display: none; }

[class*="dw-weather--"] .dw-weather-svg,
[class*="dw-weather--"] .dw-weather-temp { color: #fff; }
[class*="dw-weather--"] .dw-weather-city { color: rgba(255,255,255,0.95); }
[class*="dw-weather--"] .dw-weather-desc,
[class*="dw-weather--"] .dw-weather-detail { color: rgba(255,255,255,0.8); }
[class*="dw-weather--"] .dw-weather-feels { color: rgba(255,255,255,0.75); }
[class*="dw-weather--"] .dw-weather-detail-sep { background: rgba(255,255,255,0.4); }

.dw-weather--clear { --wc1: #F97316; --wc2: #F59E0B; --wc3: #FBBF24; }
.dw-weather--cloudy { --wc1: #3B82F6; --wc2: #60A5FA; --wc3: #93C5FD; }
.dw-weather--overcast { --wc1: #64748B; --wc2: #94A3B8; --wc3: #CBD5E1; }
.dw-weather--rain { --wc1: #1E40AF; --wc2: #3B82F6; --wc3: #60A5FA; }
.dw-weather--snow { --wc1: #6366F1; --wc2: #818CF8; --wc3: #C7D2FE; }
.dw-weather--night_clear { --wc1: #1E1B4B; --wc2: #3730A3; --wc3: #4F46E5; }

.dw-weather--night_clear .dw-weather-svg { color: rgba(255,255,255,0.9); }
.dw-weather--night_clear .dw-weather-feels { color: rgba(255,255,255,0.7); }
.dw-weather--night_clear .dw-weather-desc,
.dw-weather--night_clear .dw-weather-detail { color: rgba(255,255,255,0.75); }
.dw-weather--night_clear .dw-weather-detail-sep { background: rgba(255,255,255,0.35); }

.dw-wfx-enter-active, .dw-wfx-leave-active { transition: opacity 0.5s ease; }
.dw-wfx-enter-from, .dw-wfx-leave-to { opacity: 0; }
.dw-wicon-enter-active, .dw-wicon-leave-active { transition: opacity 0.35s ease, transform 0.35s ease; }
.dw-wicon-enter-from { opacity: 0; transform: scale(0.85); }
.dw-wicon-leave-to { opacity: 0; transform: scale(0.85); }
.dw-wtext-enter-active, .dw-wtext-leave-active { transition: opacity 0.3s ease; }
.dw-wtext-enter-from, .dw-wtext-leave-to { opacity: 0; }

.dw-weather-fx { position: absolute; inset: 0; pointer-events: none; overflow: hidden; z-index: 0; }

.dw-weather--clear .dw-weather-fx span {
    position: absolute; transform-origin: right center;
    background: linear-gradient(90deg, transparent, rgba(255, 180, 50, 0.3), transparent);
    animation: dwSunRay ease-in-out infinite;
}
.dw-weather--clear .dw-weather-fx span:nth-child(1) { width: 110%; height: 14px; top: 8%; right: 0; border-radius: 6px; transform: rotate(-10deg); animation-duration: 4s; }
.dw-weather--clear .dw-weather-fx span:nth-child(2) { width: 90%; height: 10px; top: 50%; right: 0; border-radius: 5px; transform: rotate(-5deg); animation-duration: 5s; animation-delay: 1.5s; }
.dw-weather--clear .dw-weather-fx span:nth-child(3) { width: 100%; height: 12px; top: 78%; right: 0; border-radius: 5px; transform: rotate(-8deg); animation-duration: 4.5s; animation-delay: 0.7s; }

@keyframes dwSunRay {
    0%, 100% { opacity: 0; transform: translateX(30%) rotate(-10deg); }
    40% { opacity: 0.7; }
    60% { opacity: 0.6; }
    100% { opacity: 0; transform: translateX(-80%) rotate(-10deg); }
}

.dw-weather--night_clear .dw-weather-fx span {
    position: absolute; border-radius: 50%; background: rgba(200, 200, 255, 0.15);
    animation: dwTwinkle ease-in-out infinite;
}
.dw-weather--night_clear .dw-weather-fx span:nth-child(1) { width: 3px; height: 3px; top: 15%; left: 20%; animation-duration: 2s; }
.dw-weather--night_clear .dw-weather-fx span:nth-child(2) { width: 2px; height: 2px; top: 30%; left: 70%; animation-duration: 3s; animation-delay: 0.8s; }
.dw-weather--night_clear .dw-weather-fx span:nth-child(3) { width: 2px; height: 2px; top: 60%; left: 85%; animation-duration: 2.5s; animation-delay: 1.2s; }
.dw-weather--night_clear .dw-weather-fx span:nth-child(4) { width: 3px; height: 3px; top: 75%; left: 40%; animation-duration: 2.8s; animation-delay: 0.4s; }

@keyframes dwTwinkle {
    0%, 100% { opacity: 0.1; transform: scale(0.8); }
    50% { opacity: 0.9; transform: scale(1.2); }
}

.dw-weather--rain .dw-weather-fx span {
    position: absolute; top: -16px; width: 1.5px;
    background: linear-gradient(to bottom, transparent, rgba(0, 122, 255, 0.3));
    border-radius: 0 0 2px 2px; height: 16px;
    animation: dwRainFall linear infinite;
}
.dw-weather--rain .dw-weather-fx span:nth-child(1) { left: 5%; animation-duration: 0.65s; }
.dw-weather--rain .dw-weather-fx span:nth-child(2) { left: 15%; animation-duration: 0.55s; animation-delay: 0.1s; }
.dw-weather--rain .dw-weather-fx span:nth-child(3) { left: 28%; animation-duration: 0.7s; animation-delay: 0.25s; }
.dw-weather--rain .dw-weather-fx span:nth-child(4) { left: 40%; animation-duration: 0.6s; animation-delay: 0.05s; }
.dw-weather--rain .dw-weather-fx span:nth-child(5) { left: 55%; animation-duration: 0.7s; animation-delay: 0.35s; }
.dw-weather--rain .dw-weather-fx span:nth-child(6) { left: 68%; animation-duration: 0.55s; animation-delay: 0.18s; }
.dw-weather--rain .dw-weather-fx span:nth-child(7) { left: 80%; animation-duration: 0.65s; animation-delay: 0.4s; }
.dw-weather--rain .dw-weather-fx span:nth-child(8) { left: 92%; animation-duration: 0.6s; animation-delay: 0.28s; }

@keyframes dwRainFall {
    0% { transform: translateY(-16px); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(90px); opacity: 0; }
}

.dw-weather--snow .dw-weather-fx span {
    position: absolute; top: -8px; border-radius: 50%;
    background: rgba(255, 255, 255, 0.6); width: 4px; height: 4px;
    animation: dwSnowFall linear infinite;
}
.dw-weather--snow .dw-weather-fx span:nth-child(1) { left: 8%; animation-duration: 3s; width: 5px; height: 5px; }
.dw-weather--snow .dw-weather-fx span:nth-child(2) { left: 18%; animation-duration: 2.5s; animation-delay: 0.5s; }
.dw-weather--snow .dw-weather-fx span:nth-child(3) { left: 30%; animation-duration: 3.5s; animation-delay: 1s; width: 3px; height: 3px; }
.dw-weather--snow .dw-weather-fx span:nth-child(4) { left: 42%; animation-duration: 2.8s; animation-delay: 0.3s; width: 5px; height: 5px; }
.dw-weather--snow .dw-weather-fx span:nth-child(5) { left: 55%; animation-duration: 3.2s; animation-delay: 1.5s; }
.dw-weather--snow .dw-weather-fx span:nth-child(6) { left: 67%; animation-duration: 2.7s; animation-delay: 0.8s; width: 3px; height: 3px; }
.dw-weather--snow .dw-weather-fx span:nth-child(7) { left: 80%; animation-duration: 3s; animation-delay: 0.2s; width: 5px; height: 5px; }
.dw-weather--snow .dw-weather-fx span:nth-child(8) { left: 92%; animation-duration: 2.6s; animation-delay: 1.2s; }

@keyframes dwSnowFall {
    0% { transform: translateY(-8px) translateX(0); opacity: 0; }
    10% { opacity: 0.8; }
    50% { transform: translateY(45px) translateX(8px); }
    90% { opacity: 0.6; }
    100% { transform: translateY(100px) translateX(-4px); opacity: 0; }
}

.dw-weather--cloudy .dw-weather-fx span,
.dw-weather--overcast .dw-weather-fx span {
    position: absolute; border-radius: 10px; background: rgba(120, 160, 210, 0.15);
    animation: dwCloudMove linear infinite;
}
.dw-weather--cloudy .dw-weather-fx span::before,
.dw-weather--cloudy .dw-weather-fx span::after,
.dw-weather--overcast .dw-weather-fx span::before,
.dw-weather--overcast .dw-weather-fx span::after {
    content: ''; position: absolute; background: inherit; border-radius: 50%;
}
.dw-weather--cloudy .dw-weather-fx span:nth-child(1),
.dw-weather--overcast .dw-weather-fx span:nth-child(1) { width: 50px; height: 14px; top: 12px; left: -58px; animation-duration: 9s; }
.dw-weather--cloudy .dw-weather-fx span:nth-child(1)::before,
.dw-weather--overcast .dw-weather-fx span:nth-child(1)::before { width: 22px; height: 22px; top: -12px; left: 8px; }
.dw-weather--cloudy .dw-weather-fx span:nth-child(1)::after,
.dw-weather--overcast .dw-weather-fx span:nth-child(1)::after { width: 16px; height: 16px; top: -8px; right: 8px; }
.dw-weather--cloudy .dw-weather-fx span:nth-child(2),
.dw-weather--overcast .dw-weather-fx span:nth-child(2) { width: 40px; height: 12px; top: 55%; left: -48px; animation-duration: 12s; animation-delay: 3s; }
.dw-weather--cloudy .dw-weather-fx span:nth-child(2)::before,
.dw-weather--overcast .dw-weather-fx span:nth-child(2)::before { width: 18px; height: 18px; top: -10px; left: 6px; }
.dw-weather--cloudy .dw-weather-fx span:nth-child(2)::after,
.dw-weather--overcast .dw-weather-fx span:nth-child(2)::after { width: 14px; height: 14px; top: -7px; right: 6px; }
.dw-weather--cloudy .dw-weather-fx span:nth-child(3),
.dw-weather--overcast .dw-weather-fx span:nth-child(3) { width: 44px; height: 13px; top: 30%; left: -52px; animation-duration: 7.5s; animation-delay: 5s; }
.dw-weather--cloudy .dw-weather-fx span:nth-child(3)::before,
.dw-weather--overcast .dw-weather-fx span:nth-child(3)::before { width: 20px; height: 20px; top: -11px; left: 7px; }
.dw-weather--cloudy .dw-weather-fx span:nth-child(3)::after,
.dw-weather--overcast .dw-weather-fx span:nth-child(3)::after { width: 15px; height: 15px; top: -8px; right: 7px; }

@keyframes dwCloudMove {
    0% { transform: translateX(0); opacity: 0; }
    5% { opacity: 0.8; }
    90% { opacity: 0.8; }
    100% { transform: translateX(600px); opacity: 0; }
}

.dw-card:has(.dw-weather) .dw-header { position: relative; z-index: 2; }
.dw-card:has(.dw-weather) .dw-header .dw-icon-wrap { background: rgba(255,255,255,0.2) !important; color: #fff !important; }
.dw-card:has(.dw-weather) .dw-header .dw-title { font-weight: 600; color: #fff; }
.dw-card:has(.dw-weather) .dw-header .dw-drag-handle { color: rgba(255,255,255,0.5); }

.dw-weather-content { position: relative; z-index: 1; flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding-bottom: 22px; }

.dw-weather-icon-area { width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; }
.dw-weather-svg { width: 44px; height: 44px; }
.dw-weather-svg svg { width: 100%; height: 100%; }

.dw-weather-main { display: flex; flex-direction: column; align-items: center; gap: 1px; }
.dw-weather-temp { font-size: 32px; font-weight: 700; line-height: 1.1; letter-spacing: -0.5px; color: var(--bs-body-color); }
.dw-weather-feels { font-size: 10px; color: var(--bs-gray-500); }

.dw-weather-meta { display: flex; flex-direction: column; align-items: center; gap: 1px; }
.dw-weather-city { font-size: 13px; font-weight: 600; color: var(--bs-body-color); }
.dw-weather-desc { font-size: 11px; color: var(--bs-gray-500); }

.dw-weather-details { display: flex; align-items: center; gap: 6px; margin-top: 4px; }
.dw-weather-detail { display: flex; align-items: center; gap: 3px; font-size: 10px; color: var(--bs-gray-500); }
.dw-weather-detail .bi { font-size: 10px; opacity: 0.7; }
.dw-weather-detail-sep { width: 3px; height: 3px; border-radius: 50%; background: currentColor; opacity: 0.3; }

.dw-agenda-layout { flex: 1; display: flex; flex-direction: column; gap: 14px; min-height: 0; }

.dw-agenda-next {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 12px 14px; border-radius: 10px;
    background: rgba(var(--bs-info-rgb, 114, 57, 234), 0.08);
    border: 1px solid rgba(var(--bs-info-rgb, 114, 57, 234), 0.12);
}
.dw-agenda-next-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.dw-agenda-next-time-block {
    display: flex; align-items: center; gap: 5px;
    padding: 6px 10px; border-radius: 8px;
    background: var(--bs-info); color: #fff;
    font-size: 14px; font-weight: 700; flex-shrink: 0;
    white-space: nowrap;
}
.dw-agenda-next-time-block .bi { font-size: 12px; }
.dw-agenda-next-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.dw-agenda-next-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--bs-info); }
.dw-agenda-next-title { font-size: 13px; font-weight: 600; color: var(--bs-body-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.dw-agenda-next-countdown { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
.dw-agenda-next-countdown-val { font-size: 14px; font-weight: 700; color: var(--bs-info); white-space: nowrap; }
.dw-agenda-next-countdown-lbl { font-size: 10px; color: var(--bs-gray-500); }

.dw-agenda-list { display: flex; flex-direction: column; gap: 0; max-height: 250px; overflow-y: auto; }

.dw-agenda-item {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 0;
    border-bottom: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.10);
    transition: background 0.15s;
    cursor: pointer;
}
.dw-agenda-item:last-child { border-bottom: none; }
.dw-agenda-item:hover { background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.05); }
.dw-agenda-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dw-agenda-item-time { font-size: 11px; font-weight: 700; color: var(--bs-body-color); flex-shrink: 0; width: 38px; }
.dw-agenda-item-title { font-size: 12px; font-weight: 500; color: var(--bs-body-color); flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dw-agenda-item-date { font-size: 10px; color: var(--bs-gray-500); flex-shrink: 0; white-space: nowrap; }

.dw-sms-credit-body { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px; }

.dw-sms-credit-ring { width: 120px; height: 120px; flex-shrink: 0; }
.dw-sms-credit-loaded { font-size: 11px; font-weight: 500; color: var(--bs-gray-500); margin-bottom: auto; }
.dw-sms-credit-loaded strong { font-weight: 700; color: var(--bs-body-color); opacity: 0.5; }
.dw-sms-ring-svg { width: 100%; height: 100%; }
.dw-sms-ring-svg text { text-rendering: geometricPrecision; }
html.dark .dw-sms-ring-svg text, html.midnight .dw-sms-ring-svg text { -webkit-font-smoothing: antialiased; }

.dw-sms-provider-badge {
    font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
    padding: 3px 8px; border-radius: 4px;
    background: rgba(var(--bs-success-rgb, 80, 205, 137), 0.12); color: var(--bs-success);
}
.dw-sms-credit-body > .dw-sms-provider-badge { margin-top: auto; margin-bottom: 8px; }
.dw-sms-credit-body > .dw-metrics { align-self: stretch; }
.dw-sms-provider-badge--blue {
    background: rgba(59, 130, 246, 0.1); color: #3B82F6;
}

.dw-sms-simple-body { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; }
.dw-sms-simple-hero { display: flex; align-items: baseline; gap: 6px; }
.dw-sms-simple-num { font-size: 42px; font-weight: 800; color: var(--bs-body-color); letter-spacing: -1px; line-height: 1; }
.dw-sms-simple-unit { font-size: 14px; font-weight: 600; color: var(--bs-gray-500); text-transform: uppercase; }
.dw-sms-simple-label { font-size: 11px; font-weight: 500; color: var(--bs-gray-500); }

.dw-sms-titles-body { flex: 1; display: flex; flex-direction: column; gap: 12px; }

.dw-sms-titles-tags {
    flex: 1; display: flex; flex-wrap: wrap; gap: 8px; align-content: flex-start;
}

.dw-sms-title-tag {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 6px 12px; border-radius: 8px;
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.06);
    border: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.10);
    transition: background 0.15s;
}
.dw-sms-title-tag:hover { background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12); }

.dw-sms-title-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

.dw-sms-title-name {
    font-size: 11px; font-weight: 700; color: var(--bs-body-color);
    letter-spacing: 0.5px;
}

.dw-sms-titles-footer {
    margin-top: auto; display: flex; align-items: center; gap: 8px;
    padding-top: 10px; border-top: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12);
}
.dw-sms-titles-count { font-size: 11px; font-weight: 500; color: var(--bs-gray-500); }

.dw-wo-status-body { flex: 1; display: flex; flex-direction: column; gap: 14px; }
.dw-wo-summary-section { margin-top: auto; display: flex; flex-direction: column; gap: 6px; }
.dw-wo-summary-title { font-size: 10px; font-weight: 600; color: var(--bs-gray-500); text-transform: uppercase; letter-spacing: 0.3px; }

.dw-wo-status-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
@container (max-width: 480px) { .dw-wo-status-cards { grid-template-columns: repeat(2, 1fr); } }
.dw-wo-scard {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px; border-radius: 10px;
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.06);
    border: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.10);
}
.dw-wo-scard-icon {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 15px; flex-shrink: 0;
}
.dw-wo-scard-info { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.dw-wo-scard-num { font-size: 18px; font-weight: 800; line-height: 1.1; }
.dw-wo-scard-lbl { font-size: 10px; color: var(--bs-gray-500); line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.dw-summary-bar { display: flex; height: 8px; border-radius: 4px; position: relative; }
.dw-summary-seg {
    min-width: 4px; position: relative; cursor: pointer;
    transition: opacity 0.2s, transform 0.2s;
}
.dw-summary-seg:first-child { border-radius: 4px 0 0 4px; }
.dw-summary-seg:last-child { border-radius: 0 4px 4px 0; }
.dw-summary-seg:first-child:last-child { border-radius: 4px; }
.dw-summary-seg:hover { opacity: 0.8; transform: scaleY(1.5); z-index: 2; }
.dw-summary-tooltip {
    display: none; position: absolute; bottom: calc(100% + 10px); left: 50%; transform: translateX(-50%);
    background: var(--bs-body-color); color: var(--bs-body-bg); font-size: 12px; font-weight: 600;
    padding: 6px 12px; border-radius: 8px; white-space: nowrap; pointer-events: none;
    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
    z-index: 10; line-height: 1.3; letter-spacing: 0.2px;
}
.dw-summary-tooltip::after {
    content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
    border: 5px solid transparent; border-top-color: var(--bs-body-color);
}
.dw-summary-seg:hover .dw-summary-tooltip { display: block; transform: translateX(-50%) scaleY(0.67); transform-origin: bottom center; }

.dw-wo-recent { flex: 1; display: flex; flex-direction: column; min-height: 0; }

.dw-wo-list { flex: 1; display: flex; flex-direction: column; max-height: 280px; overflow-y: auto; }

.dw-wo-item {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 7px 4px;
    border-bottom: 1px solid rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.08);
    transition: background 0.15s;
    cursor: pointer;
}
.dw-wo-item:last-child { border-bottom: none; }
.dw-wo-item:hover { background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.05); }

.dw-wo-item-left { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.dw-wo-item-customer { font-size: 12px; font-weight: 600; color: var(--bs-body-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dw-wo-item-plate { font-size: 11px; font-weight: 500; color: var(--bs-gray-500); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dw-wo-item-right { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
.dw-wo-item-amount { font-size: 13px; font-weight: 700; color: var(--bs-body-color); white-space: nowrap; }

.dw-wo-status-badge {
    display: inline-flex; align-items: center; padding: 2px 7px; border-radius: 4px;
    font-size: 9px; font-weight: 600; white-space: nowrap;
}

.dw-wo-select { width: 130px; }
.dw-wo-limit { width: 80px; }

@container (max-width: 480px) {
    .dw-header:has(.dw-wo-select) { flex-wrap: wrap; row-gap: 8px; position: relative; }
    .dw-header:has(.dw-wo-select) .dw-drag-handle { position: absolute; right: 16px; top: 14px; }
    .dw-header:has(.dw-wo-select) .dw-header-right { width: 100%; }
    .dw-wo-select { flex: 7; width: auto; min-width: 0; }
    .dw-wo-limit { flex: 3; width: auto; min-width: 0; }
}

.dw-astats-body { flex: 1; display: flex; flex-direction: column; gap: 16px; }
.dw-astats-body--compact .dw-astats-hero { gap: 12px; }
.dw-astats-body--compact .dw-astats-hero-lbl { margin-bottom: -12px; }
.dw-astats-body--compact .dw-astats-bar-section { margin-bottom: 0; }
.dw-astats-body--compact .dw-subsm-footer { margin-top: auto; }

.dw-astats-hero { display: flex; flex-direction: column; gap: 2px; margin-top: auto; }
.dw-astats-hero-num { font-size: 36px; font-weight: 800; color: var(--bs-body-color); line-height: 1; letter-spacing: -1px; }
.dw-astats-hero-lbl { font-size: 11px; color: var(--bs-gray-500); font-weight: 500; }

.dw-astats-bar-section { display: flex; flex-direction: column; gap: 4px; margin-bottom: auto; }
.dw-astats-bar { height: 8px; border-radius: 4px; background: rgba(var(--bs-warning-rgb, 255, 199, 0), 0.25); overflow: hidden; }
.dw-astats-bar-fill--ok { height: 100%; border-radius: 4px; background: var(--bs-success); transition: width 0.4s ease; }

.dw-lowstock-tbody::-webkit-scrollbar,
.dw-agenda-list::-webkit-scrollbar,
.dw-wo-list::-webkit-scrollbar { width: 4px; height: 4px; }
.dw-lowstock-tbody::-webkit-scrollbar-thumb,
.dw-agenda-list::-webkit-scrollbar-thumb,
.dw-wo-list::-webkit-scrollbar-thumb { background: var(--bs-gray-300); border-radius: 4px; }

.dw-header-right .el-select { --el-select-border-color-hover: var(--bs-border-color); }
.dw-header-right .el-input-number { --el-input-border-color: var(--bs-border-color); }
.dw-header-right .el-input__inner { font-size: 12px; }

@media (prefers-reduced-motion: reduce) {
    .dw-weather-fx span { animation: none !important; opacity: 0.15; }
    .dw-fade-in { animation: none !important; }
}

.dw-storage-body { flex: 1; display: flex; flex-direction: column; gap: 14px; }
.dw-storage-ring-section { display: flex; align-items: center; gap: 20px; }
.dw-storage-breakdown { display: flex; flex-direction: column; gap: 10px; max-height: 160px; overflow-y: auto; }
.dw-storage-breakdown-row { display: flex; flex-direction: column; gap: 4px; }
.dw-storage-breakdown-head { display: flex; align-items: center; justify-content: space-between; }
.dw-storage-breakdown-label { font-size: 12px; color: var(--bs-body-color); }
.dw-storage-breakdown-val { font-size: 12px; font-weight: 700; color: var(--bs-body-color); }
.dw-storage-bar-track { height: 6px; border-radius: 3px; background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12); overflow: hidden; }
.dw-storage-bar-fill { height: 100%; border-radius: 3px; transition: width 0.6s ease; }

@container dw-card (max-width: 220px) {
    .dw-storage-ring-section { flex-direction: column; gap: 10px; }
}

.dw-storage-simple-bar-track { background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12) !important; }
.dw-storage-simple-bar-fill { height: 100%; border-radius: 4px; transition: width 0.12s linear, background 0.4s ease; }

.dw-subs-body { display: flex; flex-direction: column; min-height: 0; flex: 1; }
.dw-subs-list { display: flex; flex-direction: column; overflow-y: auto; max-height: 240px; gap: 8px; }
.dw-subs-card {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px; border-radius: 8px;
    border-left: 3px solid var(--subs-accent, var(--bs-gray-400)); border-top-left-radius: 0; border-bottom-left-radius: 0;
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.04);
    transition: background 0.18s, box-shadow 0.18s;
}
.dw-subs-card:hover {
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.08);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.dw-subs-card--expired { opacity: 0.5; }
.dw-subs-card-left { flex-shrink: 0; }
.dw-subs-icon-wrap {
    width: 28px; height: 28px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; font-size: 14px;
}
.dw-subs-card-mid { flex: 1; min-width: 0; }
.dw-subs-card-name {
    font-size: 12.5px; font-weight: 600; color: var(--bs-body-color);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dw-subs-card-meta { margin-top: 2px; }
.dw-subs-days { font-size: 11px; color: var(--bs-gray-500); font-weight: 500; }
.dw-subs-card-right { flex-shrink: 0; padding-top: 2px; }
.dw-subs-chip {
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 9.5px; font-weight: 600; padding: 2px 8px; border-radius: 4px;
    white-space: nowrap; letter-spacing: 0.2px;
}
.dw-subs-progress {
    height: 3px; border-radius: 2px; margin-top: 6px; overflow: hidden;
    background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.10);
}
.dw-subs-progress-fill { height: 100%; border-radius: 2px; background: var(--subs-accent); transition: width 0.6s ease; }

.dw-subsm-footer { display: flex; align-items: center; gap: 12px; }
.dw-subsm-footer-item { display: flex; flex-direction: column; align-items: center; gap: 2px; flex: 1; }
.dw-subsm-footer-lbl { font-size: 11px; color: var(--bs-gray-500); }
.dw-subsm-footer-val { font-size: 13px; font-weight: 600; color: var(--bs-body-color); }
.dw-subsm-footer-sep { width: 1px; height: 28px; background: rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12); }

@property --wc1 { syntax: '<color>'; inherits: true; initial-value: transparent; }
@property --wc2 { syntax: '<color>'; inherits: true; initial-value: transparent; }
@property --wc3 { syntax: '<color>'; inherits: true; initial-value: transparent; }

html.dark .dw-weather--clear { --wc1: #C2410C; --wc2: #D97706; --wc3: #F59E0B; }
html.dark .dw-weather--cloudy { --wc1: #1E3A5F; --wc2: #2563EB; --wc3: #3B82F6; }
html.dark .dw-weather--overcast { --wc1: #374151; --wc2: #4B5563; --wc3: #6B7280; }
html.dark .dw-weather--rain { --wc1: #172554; --wc2: #1E40AF; --wc3: #3B82F6; }
html.dark .dw-weather--snow { --wc1: #312E81; --wc2: #4338CA; --wc3: #6366F1; }
html.dark .dw-weather--night_clear { --wc1: #0F0A2E; --wc2: #1E1B4B; --wc3: #3730A3; }

html.midnight .dw-weather--clear { --wc1: #92400E; --wc2: #B45309; --wc3: #D97706; }

html.cream .dw-weather--clear { --wc1: #B45309; --wc2: #D97706; --wc3: #F59E0B; }

.dashboard-grid.dw-glass-mode {
    background-image: none;
}

.dashboard-grid.dw-glass-mode .dw-card {
    background: rgba(255, 255, 255, 0.45);
    backdrop-filter: blur(64px) saturate(160%);
    -webkit-backdrop-filter: blur(64px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.50);
    border-top-color: rgba(255, 255, 255, 0.70);
    border-left-color: rgba(255, 255, 255, 0.60);
    box-shadow:
        0 0 0 0.5px rgba(0, 0, 0, 0.05),
        0 4px 24px rgba(0, 0, 0, 0.06),
        0 12px 48px rgba(0, 0, 0, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.55);
}
.dashboard-grid.dw-glass-mode .dw-card:hover {
    background: rgba(255, 255, 255, 0.52);
    box-shadow:
        0 0 0 0.5px rgba(0, 0, 0, 0.07),
        0 8px 32px rgba(0, 0, 0, 0.10),
        0 16px 56px rgba(0, 0, 0, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

html.dark .dashboard-grid.dw-glass-mode {
    background-image: none;
}
html.dark .dashboard-grid.dw-glass-mode .dw-card {
    --cg: 255, 255, 255;
    background:
        radial-gradient(ellipse 80% 70% at 10% 85%, rgba(var(--cg), 0.045), transparent 55%),
        radial-gradient(ellipse 60% 50% at 90% 15%, rgba(var(--cg), 0.03), transparent 50%),
        rgba(20, 22, 28, 0.75);
    backdrop-filter: blur(64px) saturate(120%);
    -webkit-backdrop-filter: blur(64px) saturate(120%);
    border: 1px solid rgba(var(--cg), 0.04);
    border-top-color: rgba(var(--cg), 0.08);
    border-left-color: rgba(var(--cg), 0.05);
    box-shadow:
        0 0 0 0.5px rgba(0, 0, 0, 0.4),
        0 4px 24px rgba(0, 0, 0, 0.25),
        0 12px 48px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(var(--cg), 0.04),
        inset 0 0 0 0.5px rgba(255, 255, 255, 0.02);
}
html.dark .dashboard-grid.dw-glass-mode .dw-card:hover {
    border-top-color: rgba(var(--cg), 0.12);
    box-shadow:
        0 0 0 0.5px rgba(0, 0, 0, 0.5),
        0 8px 32px rgba(0, 0, 0, 0.35),
        0 16px 56px rgba(0, 0, 0, 0.20),
        inset 0 1px 0 rgba(var(--cg), 0.06),
        inset 0 0 0 0.5px rgba(255, 255, 255, 0.03);
}

html.midnight .dashboard-grid.dw-glass-mode {
    background-image: none;
}
html.midnight .dashboard-grid.dw-glass-mode .dw-card {
    --cg: 200, 134, 60;
    background:
        radial-gradient(ellipse 80% 70% at 10% 85%, rgba(var(--cg), 0.045), transparent 55%),
        radial-gradient(ellipse 60% 50% at 90% 15%, rgba(var(--cg), 0.03), transparent 50%),
        rgba(24, 21, 18, 0.75);
    backdrop-filter: blur(64px) saturate(120%);
    -webkit-backdrop-filter: blur(64px) saturate(120%);
    border: 1px solid rgba(var(--cg), 0.04);
    border-top-color: rgba(var(--cg), 0.10);
    border-left-color: rgba(var(--cg), 0.06);
    box-shadow:
        0 0 0 0.5px rgba(0, 0, 0, 0.4),
        0 4px 24px rgba(0, 0, 0, 0.25),
        0 12px 48px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(var(--cg), 0.04),
        inset 0 0 0 0.5px rgba(200, 134, 60, 0.02);
}
html.midnight .dashboard-grid.dw-glass-mode .dw-card:hover {
    border-top-color: rgba(var(--cg), 0.14);
    box-shadow:
        0 0 0 0.5px rgba(0, 0, 0, 0.5),
        0 8px 32px rgba(0, 0, 0, 0.35),
        0 16px 56px rgba(0, 0, 0, 0.20),
        inset 0 1px 0 rgba(var(--cg), 0.06),
        inset 0 0 0 0.5px rgba(200, 134, 60, 0.03);
}

html.cream .dashboard-grid.dw-glass-mode {
    background-image: none;
}
html.cream .dashboard-grid.dw-glass-mode .dw-card {
    background: rgba(250, 247, 242, 0.55);
    backdrop-filter: blur(64px) saturate(140%);
    -webkit-backdrop-filter: blur(64px) saturate(140%);
    border: 1px solid rgba(255, 255, 255, 0.40);
    border-top-color: rgba(255, 255, 255, 0.60);
    border-left-color: rgba(255, 255, 255, 0.50);
    box-shadow:
        0 0 0 0.5px rgba(0, 0, 0, 0.03),
        0 4px 24px rgba(0, 0, 0, 0.05),
        0 12px 48px rgba(0, 0, 0, 0.03),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
}
html.cream .dashboard-grid.dw-glass-mode .dw-card:hover {
    background: rgba(250, 247, 242, 0.62);
    box-shadow:
        0 0 0 0.5px rgba(0, 0, 0, 0.05),
        0 8px 32px rgba(0, 0, 0, 0.08),
        0 16px 56px rgba(0, 0, 0, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
}
</style>
